import {
  Root as TooltipRoot,
  Trigger,
  Content,
  Arrow,
  TooltipPortal,
} from '@radix-ui/react-tooltip';
import { keyframes, styled } from 'styled-components';

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const TooltipTrigger = styled(Trigger)`
  background: none;
  border: none;
`;
const TooltipContent = styled(Content)`
  color: rgb(255 255 255 / 87%);
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 2px;
  padding: 10px 15px;
  margin: 2px;
  font-size: 14px;
  line-height: 1;
  background-color: #404854;
  box-shadow:
    hsl(206deg 22% 7% / 35%) 0 10px 38px -10px,
    hsl(206deg 22% 7% / 20%) 0 10px 20px -15px;
  user-select: none;
  animation-duration: 100ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: opacity, scale;
  transform-origin: var(--radix-tooltip-content-transform-origin);

  &[data-state='delayed-open'] {
    animation-name: ${slideDownAndFade};
  }
`;

const TooltipArrow = styled(Arrow)`
  fill: #404854;
`;

/**
 * Tooltip component
 *
 * Note that if the Tooltip is wrapping a functional component, said component must be wrapped in forwardRef
 * with the ref passed to the element to be used as the trigger. The component's props must also be spread to the
 * element, in order for the Tooltip to bind events to it.
 *
 * @param {object} props -
 * @param {React.ReactNode} props.children - trigger element for the tooltip, or the component containing it
 * @param {React.ReactNode} props.content - content to display in the tooltip
 * @param {enum} props.side - side of the trigger element to display the tooltip; default "top"
 * @returns {JSX.Element} Tooltip component
 */
export default function Tooltip({ children, content, side }) {
  return (
    <TooltipRoot>
      <TooltipTrigger
        /**
         * The "asChild" prop modifies the element type which, in this case, prevents an error from
         * being thrown about a button being rendered as the child of another button. See documentation
         * at https://www.radix-ui.com/primitives/docs/guides/composition#changing-the-element-type
         */
        asChild
      >
        {children}
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side={side}>
          <TooltipArrow />
          {content}
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  );
}
